import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import SectionCard from './SectionCard';
import RecentItems from './RecentItems';
import KpiStat from './KpiStat';
import StatusBar, { statusBarItems } from './StatusBar';
import TrendArea from './TrendArea';
import CompletionRing from './CompletionRing';
import { transactionStatusLabels } from './statusLabels';
import { formatCount } from '../overview/format';

interface ApplicationsSectionProps {
  role: 'admin' | 'editor' | 'client';
  /** The role dashboard payload (page already fetches it). */
  data: any;
}

const trendPoints = (trend: any[] | undefined) =>
  (trend || []).map((p: any) => ({
    label: String(p.period).slice(0, 10),
    value: p.count as number,
  }));

/** Applications (transactions) — stat strip + status distribution + trend + recents. */
const ApplicationsSection: React.FC<ApplicationsSectionProps> = ({ role, data }) => {
  const { t, isRTL } = useLanguage();
  const labels = transactionStatusLabels(isRTL);

  const byStatus: Record<string, number> =
    (role === 'admin' ? data?.transactions_by_status : data?.my_transactions_by_status) || {};

  const kpis = role === 'admin' ? [
    { value: data?.total_transactions, label: t('totalApplications') },
    { value: data?.active_transactions, label: isRTL ? 'نشطة' : 'Active', tone: 'primary' as const },
    { value: data?.overdue_transactions, label: isRTL ? 'متأخرة' : 'Overdue', tone: (data?.overdue_transactions > 0 ? 'error' : 'default') as 'error' | 'default' },
    { value: data?.pending_assignments, label: isRTL ? 'بانتظار التعيين' : 'Unassigned', tone: (data?.pending_assignments > 0 ? 'warning' : 'default') as 'warning' | 'default' },
  ] : role === 'editor' ? [
    { value: data?.my_assigned_transactions, label: t('assignedTasksStat') },
    { value: data?.workload_distribution?.in_progress, label: t('inProgressStat'), tone: 'primary' as const },
    { value: data?.my_completed_transactions, label: isRTL ? 'مكتملة' : 'Completed', tone: 'success' as const },
    { value: data?.my_overdue_transactions, label: isRTL ? 'متأخرة' : 'Overdue', tone: (data?.my_overdue_transactions > 0 ? 'error' : 'default') as 'error' | 'default' },
  ] : [
    { value: data?.my_total_transactions, label: t('totalApplications') },
    { value: data?.my_active_transactions, label: isRTL ? 'نشطة' : 'Active', tone: 'primary' as const },
    { value: data?.my_completed_transactions, label: isRTL ? 'مكتملة' : 'Completed', tone: 'success' as const },
    { value: data?.my_draft_transactions, label: isRTL ? 'مسودات' : 'Drafts' },
  ];

  const trends = role === 'admin' ? [
    { name: isRTL ? 'المُنشأة' : 'Created', points: trendPoints(data?.transactions_created_trend) },
    { name: isRTL ? 'المكتملة' : 'Completed', points: trendPoints(data?.transactions_completed_trend) },
  ].filter(s => s.points.length > 0) : [];

  return (
    <SectionCard
      icon="bi-clipboard-check"
      title={t('allApplications')}
      caption={isRTL ? 'حالة المعاملات' : 'Transaction status'}
      to="/transactions"
      stats={kpis.map((k, i) => (
        <KpiStat key={i} value={formatCount(k.value)} label={k.label} tone={(k as any).tone} />
      ))}
    >
      <StatusBar items={statusBarItems(byStatus, labels)} />
      {role === 'admin' && (data?.total_transactions ?? 0) > 0 && (
        <CompletionRing percent={data?.completion_rate ?? 0} label={isRTL ? 'مُنجز' : 'Done'} />
      )}
      {trends.length > 0 && (
        <div>
          <div className="dash-subtitle">{isRTL ? 'الاتجاه الزمني' : 'Trend'}</div>
          <TrendArea series={trends} />
        </div>
      )}
      <RecentItems
        endpoint="/transactions/"
        title={isRTL ? 'أحدث الطلبات' : 'Recent Applications'}
        itemLink={(t) => `/transactions/${t.id}`}
        primary={(t) => (isRTL && t.title_ar ? t.title_ar : t.title) || t.reference_number || t.transaction_id}
        secondary={(t) => t.transaction_id || t.reference_number}
        statusKey={(t) => t.status}
        labels={labels}
      />
    </SectionCard>
  );
};

export default ApplicationsSection;
